import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { getUserCampaigns, authenticateUsername, requestOTP, forgotUserPassword, verifyPassword, clearCampaigns, clearError } from '@/features/auth/authSlice'
import { OTP_TYPES } from '@/services/authService'

import './ForgotPassword.css'
import AuthLayout from './components/AuthLayout'
import ForgotPasswordForm from './components/ForgotPasswordForm'
import OTPForm from './components/OTPForm'
import CampaignSelect from './components/CampaignSelect'
import CButton from '../../components/UI/CButton'

const ForgotPassword = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { campaigns } = useSelector((state) => state.auth)

    const [step, setStep] = useState(1) // 1: username, 2: campaign select, 3: OTP + password
    const [user, setUser] = useState(null)
    const [selectedCampaign, setSelectedCampaign] = useState(null)

    useEffect(() => {
        dispatch(clearError())
    }, [dispatch])

    const handleUsernameSubmit = async (submittedUsername) => {
        try {
            const campaigns = await dispatch(getUserCampaigns(submittedUsername)).unwrap()

            if (campaigns.length === 1) {
                await handleCampaignSelect(campaigns[0])
            } else {
                setStep(2)
            }
        } catch (error) {
            console.error('Fetching campaigns failed:', error)
        }
    }

    const handleCampaignSelect = async (campaign) => {
        try {
            const userData = await dispatch(authenticateUsername({
                campaignid: campaign.campaignid,
                userid: campaign.userid,
            })).unwrap()

            setUser(userData)
            setSelectedCampaign(campaign)

            await dispatch(requestOTP({
                userid: userData.userid,
                otpTypeId: OTP_TYPES.FORGOT_PASSWORD
            })).unwrap()

            setStep(3)
        } catch (error) {
            console.error('Username authentication failed:', error)
        }
    }

    const handleBackToUsername = () => {
        dispatch(clearCampaigns())
        setStep(1)
    }

    const handleOTPSubmit = async ({ otp, password }) => {
        try {
            const trimmedPassword = password.trim()
            await dispatch(forgotUserPassword({
                userid: user.userid,
                password: trimmedPassword,
                otp: otp.trim()
            })).unwrap()

            // Auto-login with new password
            await dispatch(verifyPassword({
                userid: user.userid,
                password: trimmedPassword,
                campaignid: selectedCampaign.campaignid,
            })).unwrap()

        } catch (error) {
            console.error('Failed to reset password:', error)
        }
    }

    const handleResendOTP = async () => {
        if (!user) return false

        const result = await dispatch(requestOTP({
            userid: user.userid,
            otpTypeId: OTP_TYPES.FORGOT_PASSWORD
        }))

        return requestOTP.fulfilled.match(result)
    }

    const handleBackToLogin = () => {
        navigate('/login')
    }

    // OTP + Password screen
    if (step === 3) {
        return (
            <AuthLayout>
                <OTPForm
                    onSubmit={handleOTPSubmit}
                    onResendOTP={handleResendOTP}
                    user={user}
                    title="Reset your password"
                    subtitle={`Enter the OTP sent to ${user?.cellphonenumber || user?.emailaddress || 'your registered contact'} and create a new password.`}
                    submitLabel="Reset Password"
                />
                <CButton
                    className="back-link"
                    type="button"
                    variant="text"
                    size="large"
                    fullWidth
                    onClick={handleBackToLogin}
                    colorTheme="secondary"
                    label="Back to Login"
                />
            </AuthLayout>
        )
    }

    // Campaign selection screen
    if (step === 2) {
        return (
            <AuthLayout>
                <CampaignSelect
                    campaigns={campaigns}
                    onSelect={handleCampaignSelect}
                    onBack={handleBackToUsername}
                />
            </AuthLayout>
        )
    }

    // Username entry screen
    return (
        <AuthLayout>
            <ForgotPasswordForm
                onSubmit={handleUsernameSubmit}
                onBackToLogin={handleBackToLogin}
            />
        </AuthLayout>
    )
}

export default ForgotPassword

